import React, { useState, useCallback } from "react";
import {
    Popover,
    MenuItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
} from "@mui/material";
import { FaChevronDown } from "react-icons/fa";
import { usePostHog } from "posthog-js/react";
import axiosInstance from "../api/axiosInstance";
import { toast } from "react-toastify";
import { POSTHOG_EVENTS } from "../constants/posthog-events";
import { isCompletedStatus } from "../utils/taskUtils";

function resolveUpdatedBy(userIdProp) {
    if (userIdProp != null && userIdProp !== "") return String(userIdProp);
    try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        const id = u.userId ?? u.user_id ?? u.id ?? u.sub;
        return id != null ? String(id) : null;
    } catch {
        return null;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Status palette — colours for chip + dropdown dots
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_STYLE = {
    completed: { bg: "#dcfce7", text: "#166534", border: "#bbf7d0", dot: "#16a34a" },
    pending: { bg: "#fef9c3", text: "#854d0e", border: "#fef08a", dot: "#ca8a04" },
    delegated: { bg: "#f3e8ff", text: "#6b21a8", border: "#e9d5ff", dot: "#9333ea" },
    wip: { bg: "#ffedd5", text: "#9a3412", border: "#fed7aa", dot: "#ea580c" },
    planned: { bg: "#eff6ff", text: "#1e40af", border: "#bfdbfe", dot: "#3b82f6" },
    "in progress": { bg: "#ffedd5", text: "#9a3412", border: "#fed7aa", dot: "#ea580c" },
    "in-progress": { bg: "#ffedd5", text: "#9a3412", border: "#fed7aa", dot: "#ea580c" },
    revalidate: { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd", dot: "#0ea5e9" },
    reopen: { bg: "#fee2e2", text: "#991b1b", border: "#fecaca", dot: "#ef4444" },
};

const STATUS_FALLBACK = { bg: "#f3f4f6", text: "#374151", border: "#e5e7eb", dot: "#6b7280" };

const STATUS_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "planned", label: "Planned" },
    { value: "WIP", label: "WIP" },
    { value: "completed", label: "Completed" },
    { value: "revalidate", label: "Revalidate" },
    { value: "delegated", label: "Delegate" },
    { value: "reopen", label: "Reopen" },
];

function getStyle(status) {
    if (!status) return STATUS_FALLBACK;
    return STATUS_STYLE[status.toLowerCase()] ?? STATUS_FALLBACK;
}

// ─────────────────────────────────────────────────────────────────────────────
// InlineStatusDropdown
//
// Props:
//   task            {object}  — task object (.id, .status, .title, …)
//   onDelegateClick {fn}      — called when user picks "Delegate"
//                               receives (task) — parent opens TaskStatusModal
//   onSuccess       {fn(msg)} — called after successful inline API update
//   onCompleted     {fn(task, newStatus)} — called when status changes to Completed
//   userId          {string}  — optional current user id for analytics
//   size            "small"|"normal" — pill size variant
//   hideDelegateOption {bool} — when true, removes "Delegate" from the dropdown
// ─────────────────────────────────────────────────────────────────────────────

const InlineStatusDropdown = ({
    task,
    onDelegateClick,
    onSuccess,
    onCompleted,
    userId,
    size = "normal",
    hideDelegateOption = false,
    isMultiAssign: isMultiAssignProp,
}) => {
    const posthog = usePostHog();
    const [anchorEl, setAnchorEl] = useState(null);
    const [saving, setSaving] = useState(false);

    const currentStatus = task?.status || "Pending";
    const style = getStyle(currentStatus);

    // Determine if the task is multi-assign (grouped)
    const isActuallyGrouped =
        task?.is_grouped === 1 ||
        task?.is_grouped === "1" ||
        task?.is_grouped === true ||
        task?.hasMultipleAssignees ||
        (Array.isArray(task?.assignees) && task.assignees.length > 1) ||
        (Array.isArray(task?.tasks) && task.tasks.length > 1);

    // In deputed tasks if tasks.length < 2 then enable edit status (isMultiAssign = false)
    const isMultiAssign = isMultiAssignProp ?? (isActuallyGrouped && !(hideDelegateOption && (task?.tasks?.length || 0) < 2));

    const handleOpen = useCallback((e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    }, []);

    const handleClose = useCallback(() => setAnchorEl(null), []);

    const handleSelect = useCallback(async (opt) => {
        handleClose();

        // Delegated → open the modal instead
        if (opt.value.toLowerCase() === "delegated") {
            onDelegateClick?.(task);
            return;
        }

        // Same status → no-op
        if (opt.value.toLowerCase() === currentStatus.toLowerCase()) return;

        setSaving(true);
        try {
            const formData = new FormData();
            formData.append("status", opt.value);
            await axiosInstance.put(`/api/task/${task.id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const previousStatus = (currentStatus || "").toString().toLowerCase();
            const newStatus = (opt.value || "").toString().toLowerCase();
            posthog?.capture(POSTHOG_EVENTS.TASK_STATUS_CHANGED, {
                task_id: task.id,
                previous_value: previousStatus,
                new_value: newStatus,
                source: "inline_status_chip",
                updated_by: resolveUpdatedBy(userId),
            });
            onSuccess?.(`Status updated to "${opt.label}" successfully!`, opt.value);
            if (isCompletedStatus(opt.value)) {
                onCompleted?.(task, opt.value);
            }
        } catch (e) {
            const msg = e?.response?.data?.message || e.message || "Failed to update status.";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    }, [task, currentStatus, handleClose, onDelegateClick, onSuccess, onCompleted, posthog, userId]);

    // Filter options based on props
    const visibleOptions = hideDelegateOption
        ? STATUS_OPTIONS.filter((o) => o.value.toLowerCase() !== "delegated")
        : STATUS_OPTIONS;

    // Pill sizing
    const isSmall = size === "small";
    const chipStyle = {
        display: "inline-flex",
        alignItems: "center",
        gap: isSmall ? 4 : 5,
        padding: isSmall ? "0.2em 0.65em" : "2px 8px",
        borderRadius: "999px",
        fontSize: "var(--fs-xs)",
        fontWeight: isSmall ? 600 : 700,
        fontFamily: "inherit",
        textTransform: "capitalize",
        cursor: (saving || isMultiAssign) ? "default" : "pointer",
        userSelect: "none",
        background: style.bg,
        color: style.text,
        border: `1px solid ${style.border}`,
        transition: "transform 0.15s, box-shadow 0.15s",
        whiteSpace: "nowrap",
        lineHeight: "normal",
    };

    return (
        <>
            <span
                style={chipStyle}
                onClick={(saving || isMultiAssign) ? undefined : handleOpen}
                title={isMultiAssign ? undefined : "Click to change status"}
                className="inline-status-dropdown-chip"
            >
                {saving ? (
                    <CircularProgress size={10} sx={{ color: style.text }} />
                ) : null}

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        alignItems: "center",
                        justifyItems: "center", // Center text within the grid cell
                    }}
                >
                    <span style={{ gridArea: "1/1", visibility: "visible" }}>
                        {currentStatus}
                    </span>
                    {/* Hidden elements to force width to the longest option */}
                    {visibleOptions.map((opt) => (
                        <span
                            key={opt.value}
                            style={{
                                gridArea: "1/1",
                                visibility: "hidden",
                                height: 0,
                                overflow: "hidden",
                                userSelect: "none",
                                pointerEvents: "none",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {opt.label}
                        </span>
                    ))}
                </div>

                {!saving && !isMultiAssign && (
                    <FaChevronDown
                        size={isSmall ? 8 : 9}
                        style={{ opacity: 0.6, flexShrink: 0 }}
                    />
                )}
            </span>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.5,
                            borderRadius: "10px",
                            minWidth: 170,
                            boxShadow: "0 8px 32px rgba(0,0,0,0.13)",
                            border: "1px solid #e5e7eb",
                        },
                    },
                }}
            >
                {visibleOptions.map((opt) => {
                    const s = getStyle(opt.value);
                    const isActive =
                        currentStatus.toLowerCase() === opt.value.toLowerCase();
                    return (
                        <MenuItem
                            key={opt.value}
                            selected={isActive}
                            onClick={() => handleSelect(opt)}
                            sx={{
                                py: 1,
                                px: 2,
                                fontSize: "0.85rem",
                                fontWeight: isActive ? 700 : 500,
                                "&:hover": { bgcolor: s.bg },
                                "&.Mui-selected": { bgcolor: s.bg, color: s.text },
                                "&.Mui-selected:hover": { bgcolor: s.bg },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 24 }}>
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        background: s.dot,
                                        flexShrink: 0,
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={opt.label}
                                primaryTypographyProps={{
                                    fontSize: "0.85rem",
                                    fontWeight: isActive ? 700 : 500,
                                }}
                            />
                        </MenuItem>
                    );
                })}
            </Popover>
        </>
    );
};

export default InlineStatusDropdown;
